/* AZP-7083 — Blog post: builds the table of contents from the article headings,
   highlights the heading in view, and drives the related articles slider. */
(function () {
  'use strict';

  // Gap kept between a heading (or the sticky table of contents) and the top of the viewport.
  var GAP = 24;
  // A heading counts as current once it passes this line, below the visible header.
  var ACTIVE_OFFSET = 120;
  var HEADING_SELECTOR = 'h2, h3, h4';

  function getHeader() {
    return document.querySelector('.section-header');
  }

  // sticky-header.js reveals the header on scroll up and hides it on scroll down.
  function isStickyEnabled() {
    var stickyHeader = document.querySelector('sticky-header');
    return !!stickyHeader && stickyHeader.getAttribute('disabled') !== 'true';
  }

  function isHeaderOverlaying(header) {
    var classes = header.classList;
    var sticky = classes.contains('section-header-sticky') || classes.contains('section-header-sticky-always');
    var hidden = classes.contains('section-header-hidden') && !classes.contains('menu-open');
    return sticky && !hidden;
  }

  function headerOffset() {
    var header = getHeader();
    return header && isHeaderOverlaying(header) ? header.offsetHeight : 0;
  }

  // Exposes the height of the revealed header as --bs-article-header-offset, so the sticky
  // table of contents moves down with it instead of hiding under it.
  function syncHeaderOffset() {
    document.documentElement.style.setProperty('--bs-article-header-offset', headerOffset() + 'px');
  }

  function watchHeader() {
    var header = getHeader();
    if (!header || header.dataset.articleHeaderWatched) return;
    header.dataset.articleHeaderWatched = 'true';

    new MutationObserver(syncHeaderOffset).observe(header, { attributes: true, attributeFilter: ['class'] });
    if ('ResizeObserver' in window) new ResizeObserver(syncHeaderOffset).observe(header);
    syncHeaderOffset();
  }

  function slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-');
  }

  function headingText(heading) {
    return heading.textContent.replace(/\s+/g, ' ').trim();
  }

  // Lets wide tables scroll sideways on small screens instead of widening the page.
  function wrapTables(content) {
    content.querySelectorAll('table').forEach(function (table) {
      if (table.parentElement.classList.contains('bs-article__table')) return;
      var wrapper = document.createElement('div');
      wrapper.className = 'bs-article__table';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  function scrollToTarget(target) {
    var targetTop = target.getBoundingClientRect().top + window.pageYOffset;
    // Scrolling up reveals the sticky header, scrolling down hides it: leave room for it
    // only when it will be on screen once the scroll ends.
    var header = getHeader();
    var headerSpace = targetTop < window.pageYOffset && header && isStickyEnabled() ? header.offsetHeight : 0;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: Math.max(targetTop - headerSpace - GAP, 0), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  function initToc(root, content) {
    var nav = root.querySelector('[data-article-toc]');
    if (!nav || nav.dataset.articleTocReady) return;
    nav.dataset.articleTocReady = 'true';

    var list = nav.querySelector('[data-article-toc-list]');
    var titleItem = list.firstElementChild;
    var titleLink = titleItem.querySelector('a');
    var titleTarget = document.getElementById(titleLink.hash.slice(1)) || root;

    var headings = Array.prototype.filter.call(content.querySelectorAll(HEADING_SELECTOR), function (heading) {
      return headingText(heading) !== '';
    });
    if (!headings.length) {
      nav.remove();
      root.classList.add('bs-article--no-toc');
      return;
    }

    // The highest heading level in the article is indented as level 1, e.g. an article
    // that starts at h3 does not leave an empty h2 level.
    var topRank = Math.min.apply(
      null,
      headings.map(function (heading) {
        return Number(heading.tagName.charAt(1));
      })
    );

    var items = [{ item: titleItem, link: titleLink, target: titleTarget }];
    headings.forEach(function (heading) {
      var text = headingText(heading);
      if (!heading.id) {
        var base = slugify(text) || 'section';
        var id = base;
        var suffix = 2;
        while (document.getElementById(id)) id = base + '-' + suffix++;
        heading.id = id;
      }

      var item = document.createElement('li');
      item.className = 'bs-article-toc__item';
      item.dataset.level = String(Math.min(Number(heading.tagName.charAt(1)) - topRank + 1, 3));

      var link = document.createElement('a');
      link.className = 'bs-article-toc__link';
      link.href = '#' + encodeURIComponent(heading.id);
      link.textContent = text;

      item.appendChild(link);
      list.appendChild(item);
      items.push({ item: item, link: link, target: heading });
    });

    var active = items[0];

    // Keeps the active entry visible when the table of contents is taller than the viewport.
    function revealInNav(item) {
      if (nav.scrollHeight <= nav.clientHeight) return;
      var top = item.offsetTop;
      var bottom = top + item.offsetHeight;
      if (top < nav.scrollTop) {
        nav.scrollTop = top;
      } else if (bottom > nav.scrollTop + nav.clientHeight) {
        nav.scrollTop = bottom - nav.clientHeight;
      }
    }

    function setActive(next) {
      if (next === active) return;
      active.item.classList.remove('is-active');
      active.link.removeAttribute('aria-current');
      next.item.classList.add('is-active');
      next.link.setAttribute('aria-current', 'true');
      active = next;
      revealInNav(next.item);
    }

    function update() {
      var line = headerOffset() + ACTIVE_OFFSET;
      var current = items[0];
      for (var i = 1; i < items.length; i++) {
        if (items[i].target.getBoundingClientRect().top > line) break;
        current = items[i];
      }
      setActive(current);
    }

    items.forEach(function (entry) {
      entry.link.addEventListener('click', function (event) {
        event.preventDefault();
        scrollToTarget(entry.target);
        history.replaceState(null, '', entry.link.hash);
        setActive(entry);
      });
    });

    var ticking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          update();
        });
      },
      { passive: true }
    );

    update();
  }

  function initRelated(root) {
    var track = root.querySelector('[data-article-related-track]');
    if (!track || track.dataset.articleRelatedReady) return;
    track.dataset.articleRelatedReady = 'true';

    var prev = root.querySelector('[data-article-related-prev]');
    var next = root.querySelector('[data-article-related-next]');

    function step() {
      var item = track.firstElementChild;
      var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      return item ? item.getBoundingClientRect().width + gap : track.clientWidth;
    }

    function sync() {
      var max = track.scrollWidth - track.clientWidth;
      prev.hidden = track.scrollLeft <= 1;
      next.hidden = track.scrollLeft >= max - 1;
    }

    prev.addEventListener('click', function () {
      track.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    next.addEventListener('click', function () {
      track.scrollBy({ left: step(), behavior: 'smooth' });
    });

    var ticking = false;
    function onChange() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        sync();
      });
    }
    track.addEventListener('scroll', onChange, { passive: true });
    window.addEventListener('resize', onChange);
    sync();
  }

  function initAll() {
    watchHeader();
    document.querySelectorAll('[data-article]').forEach(function (root) {
      var content = root.querySelector('[data-article-content]');
      if (content) {
        wrapTables(content);
        initToc(root, content);
      }
      initRelated(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
